/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Dropdown, DropdownSkeleton, FormGroup, ComboBox } from '@carbon/react';
import cx from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '../../../store';
import { getIndices, getFields, listCollections } from '../store/actions';
import {
  setDatabase,
  setCollection,
  setModelId,
  setIndex,
  clearCollection,
  clearIndex,
  setDraftTitleOptionValue,
  setDraftBodyOptionValue,
  setDraftUrlOptionValue
} from '../store/reducer';
import {
  selectDataSources,
  selectCollections,
  selectDraftCollection,
  selectDraftDatabase,
  selectDraftIndex,
  selectDraftModelId,
  selectDataSourcesLoading,
  selectCollectionsLoading,
  selectGetIndicesLoading,
  selectGetModelsLoading,
  selectIndices,
  selectModels,
  selectDraftTitleOptionValue,
  selectDraftBodyOptionValue,
  selectDraftUrlOptionValue,
  selectFields,
  selectFieldsLoading
} from '../store/selectors';
import { MilvusDataSourceMessages as messages } from './messages';

import './MilvusDataSource.scss';
interface MilvusDataSourceProps {
  showTab: boolean;
  skillId: string;
  assistantId: string;
  readOnly: boolean;
}

export const MilvusDataSource = ({
  showTab,
  skillId,
  assistantId,
  readOnly
}: MilvusDataSourceProps) => {
  const dispatch = useDispatch();
  const { formatMessage: t } = useIntl();

  const dataSources = useAppSelector(selectDataSources);
  const collectionsList = useAppSelector(selectCollections);
  const databases = dataSources?.db_names || [];
  const collections = collectionsList?.collection_names || [];
  const indices = useAppSelector(selectIndices);
  const title = useAppSelector(selectDraftTitleOptionValue);
  const body = useAppSelector(selectDraftBodyOptionValue);
  const url = useAppSelector(selectDraftUrlOptionValue);
  const fields = useAppSelector(selectFields) || [];
  const mappedIndices =
    indices?.index_descriptions?.map(item => item.index_name) || [];
  const models = useAppSelector(selectModels);

  const database = useAppSelector(selectDraftDatabase);
  const modelId = useAppSelector(selectDraftModelId);

  const model = models.find(item => item.model_id === modelId);
  const collection = useAppSelector(selectDraftCollection);
  const index = useAppSelector(selectDraftIndex);
  const getIndicesLoading = useAppSelector(selectGetIndicesLoading);
  const getCollectionsLoading = useAppSelector(selectCollectionsLoading);
  const getDataSourcesLoading = useAppSelector(selectDataSourcesLoading);
  const getModelsLoading = useAppSelector(selectGetModelsLoading);

  const mappedFields = fields.map(item => item.name);

  const getFieldsLoading = useAppSelector(selectFieldsLoading);

  const titleSelection = item => {
    const title = item?.selectedItem;

    dispatch(setDraftTitleOptionValue(title));
  };
  const bodySelection = item => {
    const body = item?.selectedItem;

    dispatch(setDraftBodyOptionValue(body));
  };

  const urlSelection = item => {
    const url = item?.selectedItem;

    if (url === null) {
      dispatch(setDraftUrlOptionValue(''));
    } else {
      dispatch(setDraftUrlOptionValue(url));
    }
  };

  const databaseSelection = item => {
    const database = item?.selectedItem;

    dispatch(setDatabase(database));
    dispatch(listCollections({ skillId, assistantId, database: database }));
    // Clear collection selection
    dispatch(clearCollection());
  };
  const collectionSelection = item => {
    const collectionName = item?.selectedItem;
    dispatch(setCollection(collectionName));
    dispatch(clearIndex());
    dispatch(getIndices({ skillId, assistantId, collectionName }));
    dispatch(getFields({ skillId, assistantId, collectionName }));
  };
  const indexSelection = item => {
    const index = item?.selectedItem;
    dispatch(setIndex(index));
  };
  const modelSelection = item => {
    const model = item?.selectedItem;
    dispatch(setModelId(model.model_id));
  };

  return (
    <div
      className={cx('MilvusDataSource', {
        'MilvusDataSource__no-padding': showTab
      })}>
      <div
        className={cx('MilvusDataSource__content-container', {
          'MilvusDataSource__content-container-tabs': showTab
        })}>
        <div
          className={cx('MilvusDataSource__title', {
            'MilvusDataSource__title-tab': showTab
          })}>
          <FormattedMessage {...messages.title} />
        </div>
        <FormGroup legendText="" className="MilvusDataSource__form">
          {getDataSourcesLoading ? (
            <DropdownSkeleton />
          ) : (
            <Dropdown
              id="MilvusDataSource__database"
              label={t(messages.selectOne)}
              titleText={t(messages.database)}
              items={databases}
              selectedItem={database}
              onChange={databaseSelection}
              readOnly={readOnly}
            />
          )}
          {getCollectionsLoading ? (
            <DropdownSkeleton />
          ) : (
            <Dropdown
              id="MilvusDataSource__collection"
              label={t(messages.selectOne)}
              titleText={t(messages.collection)}
              items={collections}
              selectedItem={collection}
              onChange={collectionSelection}
              disabled={database === ''}
              readOnly={readOnly}
            />
          )}
          {getIndicesLoading ? (
            <DropdownSkeleton />
          ) : (
            <Dropdown
              id="MilvusDataSource__index"
              label={t(messages.selectOne)}
              titleText={t(messages.index)}
              items={mappedIndices}
              selectedItem={index}
              onChange={indexSelection}
              disabled={!collection || collection === ''}
              readOnly={readOnly}
            />
          )}
          {getModelsLoading ? (
            <DropdownSkeleton />
          ) : (
            <Dropdown
              id="MilvusDataSource__model"
              label={t(messages.selectOne)}
              titleText={t(messages.modelId)}
              items={models}
              itemToString={item => (item ? item.display_name : '')}
              selectedItem={model}
              onChange={modelSelection}
              readOnly={readOnly}
            />
          )}
        </FormGroup>
        <>
          <div
            className={cx('ConfigureContent__title', {
              'ConfigureContent__title-tab': showTab
            })}>
            <h4>
              <FormattedMessage {...messages.contentTitle} />
            </h4>
          </div>
          <div
            className={cx('ConfigureContent__description', {
              'ConfigureContent__description-tab': showTab
            })}>
            <FormattedMessage
              {...messages.description}
              values={{ name: 'Milvus' }}
            />
          </div>
        </>
        <FormGroup legendText="" className="MilvusDataSource__form">
          {getFieldsLoading ? (
            <>
              <DropdownSkeleton />
              <DropdownSkeleton />
              <DropdownSkeleton />
            </>
          ) : (
            <>
              <Dropdown
                id="ConfigureContent__form-title-input"
                label={t(messages.selectOne)}
                titleText={t(messages.fieldTitle)}
                items={mappedFields}
                selectedItem={title}
                onChange={titleSelection}
                disabled={!collection}
                readOnly={readOnly}
              />
              <Dropdown
                id="ConfigureContent__form-body-input"
                label={t(messages.selectOne)}
                titleText={t(messages.fieldBody)}
                items={mappedFields}
                selectedItem={body}
                onChange={bodySelection}
                disabled={!collection}
                readOnly={readOnly}
              />
              <ComboBox
                id="ConfigureContent__form-body-input"
                placeholder={t(messages.selectOne)}
                titleText={t(messages.fieldUrl)}
                items={mappedFields}
                selectedItem={url}
                onChange={urlSelection}
                disabled={!collection}
                readOnly={readOnly}
              />
            </>
          )}
        </FormGroup>
      </div>
    </div>
  );
};
